import * as React from "react"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Users,
  FileText,
  BarChart,
  LogOut,
  ChevronDown,
} from "lucide-react"

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "./ui/sidebar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"
import { Avatar, AvatarFallback } from "./ui/avatar"
import { useAuth } from "../contexts/AuthContext"
import { useAIModel } from "../contexts/AIModelContext"
import { useTranslation } from "react-i18next"
import { LanguageSwitcher } from "./LanguageSwitcher"

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const { selectedModel } = useAIModel()
  const { isMobile } = useSidebar()

  // Determine active tab based on path
  const activeTab = React.useMemo(() => {
    if (location.pathname.startsWith("/dashboard")) return "patients"
    if (location.pathname.startsWith("/research")) return "research"
    if (location.pathname.startsWith("/reports")) return "reports"
    return ""
  }, [location.pathname])

  const navItems = [
    {
      title: t("navigation.patients"),
      url: "/dashboard",
      icon: Users,
      id: "patients",
    },
    {
      title: t("navigation.research"),
      url: "/research",
      icon: FileText,
      id: "research",
    },
    {
      title: t("navigation.reports"),
      url: "/reports",
      icon: BarChart,
      id: "reports",
    },
  ]

  const handleLogout = () => {
    logout()
  }

  return (
    <Sidebar variant="inset" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <a href="/">
                <div className="flex aspect-square size-8 items-center justify-center rounded-sm bg-slate-100 text-primary-foreground">
                  <img src="/favicon.ico" alt="Logo" className="size-5" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{t("navigation.appTitle")}</span>
                  <span className="truncate text-xs">Medical AI Assistant</span>
                </div>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{t("navigation.navTitle")}</SidebarGroupLabel>
          <SidebarMenu>
            {navItems.map((item) => (
              <SidebarMenuItem key={item.id}>
                <SidebarMenuButton
                  tooltip={item.title}
                  isActive={activeTab === item.id}
                  onClick={() => navigate(item.url)}
                >
                  <item.icon />
                  <span>{item.title}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarFallback className="rounded-lg bg-primary/10 text-primary">
                      {user?.username?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || "?"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{user?.username || user?.email}</span>
                    <span className="truncate text-xs">{selectedModel === 'deepseek' ? 'DeepSeek' : 'CMI DeepSeek'}</span>
                  </div>
                  <ChevronDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side={isMobile ? "bottom" : "right"}
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarFallback className="rounded-lg bg-primary/10 text-primary">
                        {user?.username?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">{user?.username || user?.email}</span>
                      <span className="truncate text-xs">{user?.email}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {/* <DropdownGroup>
                  <DropdownMenuLabel className="text-xs text-muted-foreground px-2 py-1.5">{t('navigation.aiModelSelection')}</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => setSelectedModel('deepseek')}>
                    <Brain className="mr-2 size-4" />
                    <span>DeepSeek</span>
                    {selectedModel === 'deepseek' && <Check className="ml-auto size-4" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSelectedModel('cmi')}>
                    <Brain className="mr-2 size-4" />
                    <span>CMI DeepSeek</span>
                    {selectedModel === 'cmi' && <Check className="ml-auto size-4" />}
                  </DropdownMenuItem>
                </DropdownGroup>
                <DropdownMenuSeparator /> */}
                <DropdownGroup>
                   <div className="px-2 py-1.5">
                    <LanguageSwitcher className="w-full justify-start h-8 px-2 font-normal" />
                  </div>
                </DropdownGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 size-4" />
                  <span>{t('navigation.logout')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}

function DropdownGroup({ children }: { children: React.ReactNode }) {
  return <div className="p-1">{children}</div>
}
